import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/isp-web-component-library';
import type { EnrichedParticipants } from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import React from 'react';

import type { FootballFixture } from '@swf/generated/graphql/scoreboards/schemas';
import { EventTypeId } from '@swf/sports/types/eventType';

import ScoreboardWrapper from '../ScoreboardWrapper/ScoreboardWrapper';

import { TXTWCL_Penalties } from './Football.messages.json';
import styles from './Football.scss';

interface Props {
  enrichedParticipants?: EnrichedParticipants;
  eventTime: string;
  footballFixture?: FootballFixture;
  eventId: number;
}

const getParticipantScore = (
  participant: EnrichedParticipants[number],
  score: FootballFixture['score'] | undefined
) => (participant.type === 'HOME' ? score?.home : score?.away);

const getParticipantPenaltyScore = (
  participant: EnrichedParticipants[number],
  penaltyShootout?: FootballFixture['penaltyShootout'] | undefined
) =>
  penaltyShootout?.penaltyScores?.filter(
    (penaltyScore) => penaltyScore.side === participant.type && penaltyScore.shotResult === 'SCORE'
  ).length;

const Football: React.FC<Props> = ({
  enrichedParticipants,
  footballFixture,
  eventTime,
  eventId
}) => {
  const [participantA, participantB] = enrichedParticipants ?? [];
  const currentScore = participantA.type && participantB.type ? footballFixture?.score : null;
  const penaltyShootout =
    participantA.type && participantB.type ? footballFixture?.penaltyShootout : null;

  const penaltyTakingTeam = penaltyShootout?.nextTeamToShoot;

  return (
    <ScoreboardWrapper eventId={eventId} eventTime={eventTime} eventTypeId={EventTypeId.SOCCER}>
      {penaltyShootout && (
        <div className={styles.pens}>
          <Translation {...TXTWCL_Penalties} />
        </div>
      )}
      <Scoreboard>
        <ScoreboardParticipants>
          <ScoreboardParticipant
            name={participantA.participant}
            isVisibleMarker={penaltyTakingTeam === participantA.type}
          />
          <ScoreboardParticipant
            name={participantB.participant}
            isVisibleMarker={penaltyTakingTeam === participantB.type}
          />
        </ScoreboardParticipants>
        <ScoreboardScores>
          {penaltyShootout ? (
            <>
              <ScoreboardScore
                score={[
                  getParticipantScore(participantA, currentScore) ?? null,
                  getParticipantScore(participantB, currentScore) ?? null
                ]}
              />
              <ScoreboardScore
                score={[
                  getParticipantPenaltyScore(participantA, penaltyShootout) ?? null,
                  getParticipantPenaltyScore(participantB, penaltyShootout) ?? null
                ]}
                isDoubleDigit
              />
            </>
          ) : (
            <ScoreboardScore
              score={[
                getParticipantScore(participantA, currentScore) ?? null,
                getParticipantScore(participantB, currentScore) ?? null
              ]}
            />
          )}
        </ScoreboardScores>
      </Scoreboard>
    </ScoreboardWrapper>
  );
};

export default Football;
